/**
 * Source-editor counterpart to the preview's CollapseEnricher.
 *
 * A fenced code block preceded by a `<!-- collapse ... -->` marker is collapsed
 * by default in the CodeMirror source editor too, so a huge (e.g. 1000-line)
 * script doesn't make scrolling painful. This reuses CodeMirror's built-in code
 * folding:
 *  - `collapseFold()` configures a labelled fold placeholder ("⋯ N lines").
 *  - `autoFoldCollapsedBlocks()` folds every marked block on fresh load.
 *
 * Because folding state lives in the EditorState (and maps through edits), a
 * block the user manually expands stays expanded, and per-tab cached states
 * restore folds as the user left them. Clicking the placeholder — or the fold
 * gutter chevron — expands; the gutter chevron collapses it again.
 */

import { EditorView } from '@codemirror/view';
import { codeFolding, foldEffect, foldedRanges } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import { computeCollapseFoldLines } from './collapse-scan.js';

interface Prepared {
  lines: number;
}

/**
 * Configure code folding with a labelled placeholder. Safe to add alongside the
 * default `codeFolding()` that `basicSetup`'s `foldGutter()` already installs:
 * both reference the same fold state field (deduplicated), and this is the only
 * contributor to the fold config facet, so its placeholder wins.
 */
export function collapseFold(): Extension {
  return codeFolding({
    preparePlaceholder: (state, range): Prepared => {
      const startLine = state.doc.lineAt(range.from);
      const endLine = state.doc.lineAt(range.to).number;
      // When the fold begins at a line start (our auto-fold, which keeps the
      // last visible line's newline out of the range so the placeholder lands on
      // its own line), that first line is fully hidden → count it. A mid-line
      // fold (e.g. a manual gutter fold) leaves its start line partly visible.
      const atLineStart = range.from === startLine.from;
      const hidden = endLine - startLine.number + (atLineStart ? 1 : 0);
      return { lines: Math.max(1, hidden) };
    },
    placeholderDOM: (_view, onclick, prepared) => {
      const el = document.createElement('span');
      el.className = 'cm-foldPlaceholder cm-collapse-fold';
      const lines = (prepared as Prepared | null)?.lines;
      el.textContent = lines ? `⋯ ${lines} more lines — click to expand` : '⋯ click to expand';
      el.title = 'Click to expand';
      el.setAttribute('aria-label', 'Expand collapsed code');
      el.onclick = onclick;
      return el;
    },
  });
}

/**
 * Fold every `<!-- collapse -->`-marked fenced code block in the document.
 * Call after loading FRESH content into the editor (not when restoring a cached
 * per-tab state, which already carries the user's fold choices).
 *
 * Mirrors the preview's CollapseEnricher: the first N code lines stay visible
 * (N from the marker's `| N`, default 15) and only the overflow is folded, into
 * a `⋯ M lines` placeholder at the end of the last visible line. Blocks with at
 * most N code lines are left fully expanded, just like the preview. The opening
 * and closing fences remain visible. Idempotent: re-folding an already-folded
 * range is a no-op in the fold state.
 *
 * The fold starts at the START of the first hidden line (not the end of the
 * last visible one), so the Nth line keeps its own newline and the placeholder
 * renders on its own line — letting it stay inline-block instead of block (a
 * block child in the inline .cm-line spawns anonymous block boxes that add
 * stray pre/post whitespace).
 */
export function autoFoldCollapsedBlocks(view: EditorView): void {
  const { doc } = view.state;
  const ranges = computeCollapseFoldLines(doc.toString().split('\n'))
    .map(({ firstHiddenLine, lastHiddenLine }) => ({
      from: doc.line(firstHiddenLine).from,
      to: doc.line(lastHiddenLine).to,
    }))
    .filter((r) => r.to > r.from);

  if (!ranges.length) return;

  // Skip ranges already folded to avoid churn (e.g. a repeated call).
  const folded = foldedRanges(view.state);
  const effects = ranges
    .filter((r) => {
      let exists = false;
      folded.between(r.from, r.to, (a, b) => {
        if (a === r.from && b === r.to) exists = true;
      });
      return !exists;
    })
    .map((r) => foldEffect.of(r));

  if (effects.length) view.dispatch({ effects });
}
